import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMergeConflicts, mergeAgents, markNoMergeNeeded } from '../api/agentService';

function AgentMergeConflicts() {
  const navigate = useNavigate();
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedConflict, setExpandedConflict] = useState(null);
  const [selectedValues, setSelectedValues] = useState({});
  const [processingMerges, setProcessingMerges] = useState({});
  const [markingNoMerge, setMarkingNoMerge] = useState({});
  const [message, setMessage] = useState('');
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);

  // Load conflicts from backend
  const loadConflicts = async () => {
    try {
      setIsPageLoading(true);

      const data = await fetchMergeConflicts();
      setConflicts(data.conflicts);

      // Initialize default selections
      const initialSelections = {};
      data.conflicts.forEach((conflict, conflictIndex) => {
        initialSelections[conflictIndex] = {};
        const { agent1, agent2 } = conflict;
        // For each field that might differ, pick a sensible default
        ['title', 'name', 'address', 'email'].forEach((field) => {
          if (agent1[field] !== agent2[field]) {
            if (agent1[field] == null && agent2[field] != null) {
              initialSelections[conflictIndex][field] = agent2[field];
            } else if (agent1[field] != null && agent2[field] == null) {
              initialSelections[conflictIndex][field] = agent1[field];
            } else {
              // Both are non-null but different; default to agent1
              initialSelections[conflictIndex][field] = agent1[field];
            }
          }
        });
      });

      setSelectedValues(initialSelections);
      setLoading(false);

      // Slight delay for smoother overlay transition
      setTimeout(() => {
        setIsPageLoading(false);

        // Scroll to top after loading
        const container = document.querySelector('.insolvency-container');
        if (container) {
          container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } catch (err) {
      console.error('Error fetching merge conflicts:', err);
      setError(err.message || 'Failed to load merge conflicts');
      setLoading(false);
      setIsPageLoading(false);
      setMessage('Error loading merge conflicts. Please try again.');
    }
  };

  // Notification fade in/out
  useEffect(() => {
    if (message) {
      setIsNotificationVisible(true);

      // Start fade out after 4s
      const timer = setTimeout(() => {
        setIsNotificationVisible(false);
        // Clear message after fade
        setTimeout(() => setMessage(''), 500);
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  // Initial fetch of conflicts
  useEffect(() => {
    loadConflicts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectionChange = (conflictIndex, field, value) => {
    setSelectedValues((prev) => ({
      ...prev,
      [conflictIndex]: {
        ...prev[conflictIndex],
        [field]: value,
      },
    }));
  };

  // Merge handler
  const handleMerge = async (conflictIndex) => {
    const conflict = conflicts[conflictIndex];
    const { agent1, agent2 } = conflict;
    const selections = selectedValues[conflictIndex] || {};

    try {
      setProcessingMerges((prev) => ({ ...prev, [conflictIndex]: true }));

      const mergedAgent = {
        // Use agent1's ID as primary
        agent_id: agent1.agent_id,
        // For each field, use the selected value or whichever is non-null if not in selections
        title:
          selections.title !== undefined
            ? selections.title
            : agent1.title || agent2.title,
        name:
          selections.name !== undefined
            ? selections.name
            : agent1.name || agent2.name,
        address:
          selections.address !== undefined
            ? selections.address
            : agent1.address || agent2.address,
        email:
          selections.email !== undefined
            ? selections.email
            : agent1.email || agent2.email,
        // Keep other metadata from agent1 (like blacklist)
        blacklist: agent1.blacklist || agent2.blacklist,
      };

      await mergeAgents(agent1.agent_id, agent2.agent_id, mergedAgent);
      await loadConflicts(); // reload all
      setExpandedConflict(null);
      setMessage(`Agents merged successfully! Primary agent ID: ${agent1.agent_id}`);
    } catch (error) {
      console.error('Error merging agents:', error);
      setMessage(`Failed to merge agents: ${error.message || 'Unknown error'}`);
    } finally {
      setProcessingMerges((prev) => {
        const updated = { ...prev };
        delete updated[conflictIndex];
        return updated;
      });
    }
  };

  // No merge needed handler
  const handleMarkNoMerge = async (conflictIndex) => {
    const conflict = conflicts[conflictIndex];
    const { agent1, agent2 } = conflict;

    try {
      setMarkingNoMerge((prev) => ({ ...prev, [conflictIndex]: true }));
      await markNoMergeNeeded(agent1.agent_id, agent2.agent_id);

      await loadConflicts();
      setExpandedConflict(null);
      setMessage('Agents marked as no merge needed.');
    } catch (error) {
      console.error('Error marking no merge:', error);
      setMessage(`Failed to mark as no merge: ${error.message || 'Unknown error'}`);
    } finally {
      setMarkingNoMerge((prev) => {
        const updated = { ...prev };
        delete updated[conflictIndex];
        return updated;
      });
    }
  };

  // Renders a single row with a dropdown for selecting which value to keep
  const renderRow = ({
    field,
    label,
    agent1,
    agent2,
    conflictIndex,
    selectedValues,
    handleSelectionChange,
    isBoolean = false,
  }) => {
    const agent1Value = agent1[field];
    const agent2Value = agent2[field];
    const bothEqual = agent1Value === agent2Value;

    // If a user-chosen selection exists, otherwise fallback
    const selectedValue =
      selectedValues[conflictIndex]?.[field] !== undefined
        ? selectedValues[conflictIndex][field]
        : agent1Value || agent2Value;

    // Display text for agent 1 & 2
    const displayAgent1Value = isBoolean
      ? agent1Value
        ? 'Yes'
        : 'No'
      : agent1Value || '-';
    const displayAgent2Value = isBoolean
      ? agent2Value
        ? 'Yes'
        : 'No'
      : agent2Value || '-';
    const displaySelectedValue = isBoolean
      ? selectedValue
        ? 'Yes'
        : 'No'
      : selectedValue || '-';

    // If values differ, let the user choose
    if (!bothEqual) {
      if (isBoolean) {
        // True/False => Yes/No
        // Possibly agent1 is true/false and agent2 is the opposite
        // We'll show a dropdown with "Yes" or "No"
        return (
          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
            <td style={{ padding: '8px', fontWeight: 600 }}>{label}</td>
            <td style={{ padding: '8px' }}>{displayAgent1Value}</td>
            <td style={{ padding: '8px' }}>{displayAgent2Value}</td>
            <td style={{ padding: '8px' }}>
              <select
                value={String(selectedValue)}
                onChange={(e) =>
                  handleSelectionChange(
                    conflictIndex,
                    field,
                    e.target.value === 'true'
                  )
                }
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </td>
          </tr>
        );
      } else {
        // Show a dropdown with agent1Value and agent2Value
        return (
          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
            <td style={{ padding: '8px', fontWeight: 600 }}>{label}</td>
            <td style={{ padding: '8px' }}>{displayAgent1Value}</td>
            <td style={{ padding: '8px' }}>{displayAgent2Value}</td>
            <td style={{ padding: '8px' }}>
              <select
                value={selectedValue || ''}
                onChange={(e) =>
                  handleSelectionChange(conflictIndex, field, e.target.value)
                }
              >
                <option value={agent1Value || ''}>
                  Agent 1: {agent1Value || '(Empty)'}
                </option>
                <option value={agent2Value || ''}>
                  Agent 2: {agent2Value || '(Empty)'}
                </option>
              </select>
            </td>
          </tr>
        );
      }
    }

    // If both agent1Value and agent2Value are the same, just display one line
    return (
      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
        <td style={{ padding: '8px', fontWeight: 600 }}>{label}</td>
        <td style={{ padding: '8px' }}>{displayAgent1Value}</td>
        <td style={{ padding: '8px' }}>{displayAgent2Value}</td>
        <td style={{ padding: '8px' }}>
          <span>{displaySelectedValue}</span>
        </td>
      </tr>
    );
  };

  // Initial loading screen
  if (loading) {
    return (
      <div
        className="insolvency-container"
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h2 style={{ marginBottom: '1.5rem' }}>
          Possible duplicated Insolvency Agents
        </h2>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '3rem',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '50%',
              borderTop: '4px solid #1890ff',
              animation: 'spin 1s linear infinite',
              marginBottom: '1rem',
            }}
          ></div>
          <p style={{ fontSize: '1.1rem', color: '#666' }}>
            Loading potential merge conflicts...
          </p>
        </div>
      </div>
    );
  }

  // Error screen
  if (error) {
    return (
      <div
        className="insolvency-container"
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h2 style={{ marginBottom: '1.5rem' }}>
          Possible duplicated Insolvency Agents
        </h2>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '3rem',
            backgroundColor: '#fff1f0',
            border: '1px solid #ffa39e',
            borderRadius: '8px',
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            fill="none"
            viewBox="0 0 24 24"
            stroke="#ff4d4f"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p style={{ fontSize: '1.1rem', color: '#cf1322', marginTop: '1rem' }}>
            Error: {error}
          </p>
          <button
            onClick={() => loadConflicts()}
            style={{
              marginTop: '1.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#fff',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No conflicts found
  if (conflicts.length === 0) {
    return (
      <div
        className="insolvency-container"
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h2 style={{ marginBottom: '1.5rem' }}>
          Possible duplicated Insolvency Agents
        </h2>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '3rem',
            backgroundColor: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: '8px',
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            fill="none"
            viewBox="0 0 24 24"
            stroke="#52c41a"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p
            style={{ fontSize: '1.1rem', color: '#389e0d', marginTop: '1rem' }}
          >
            No potential merge conflicts found.
          </p>
          <button 
            className="button-bin"
            onClick={() => navigate('/register3')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Insolvency Agents
          </button>
        </div>
      </div>
    );
  }

  // Main UI
  return (
    <div
      className="insolvency-container"
      style={{ maxWidth: '1200px', margin: '0 auto' }}
    >
      <h2
        style={{
          marginBottom: '1.5rem',
          borderBottom: '1px solid #e8e8e8',
          paddingBottom: '0.75rem',
        }}
      >
        Possible duplicated Insolvency Agents
      </h2>

      {/* Notification System with Smooth Fade */}
      {message && (
        <div
          style={{
            position: 'fixed',
            top: 60,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '90%',
            maxWidth: '800px',
            zIndex: 1000,
            padding: '1rem',
            backgroundColor: '#e6f7ff',
            border: '1px solid #91d5ff',
            borderRadius: '4px',
            boxShadow: '0px 4px 8px rgba(0,0,0,0.1)',
            opacity: isNotificationVisible ? 1 : 0,
            transition: 'opacity 0.5s ease-in-out',
          }}
        >
          {message}
        </div>
      )}

      {/* Main "card" container */}
      <div
        style={{
          background: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          position: 'relative',
        }}
      >
        {/* Page Loading Overlay */}
        {isPageLoading && (
          <div
            style={{
              textAlign: 'center',
              padding: '1rem',
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 10,
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: '30px',
                height: '30px',
                border: '3px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '50%',
                borderTop: '3px solid #1890ff',
                animation: 'spin 1s linear infinite',
              }}
            ></div>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '1.5rem',
            padding: '0 0 1rem 0',
            borderBottom: '1px solid #f0f0f0',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <button
              onClick={() => navigate('/register3')}
              className="button-bin"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Insolvency Agents
            </button>
          </div>
        </div>

        <p
          style={{
            marginBottom: '1.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: '4px',
            fontSize: '14px',
          }}
        >
          The following pairs of agents have similar names and might be
          duplicates. Click to expand each pair, review details, and choose a
          final merged result if appropriate.
        </p>

        {conflicts.map((conflict, index) => {
          const { agent1, agent2 } = conflict;
          const isExpanded = expandedConflict === index;

          return (
            <div
              key={index}
              style={{
                marginBottom: '1rem',
                border: '1px solid #f0f0f0',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
              }}
            >
              <div
                style={{
                  padding: '1rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  backgroundColor: isExpanded ? '#f0f8ff' : '#fff',
                  borderBottom: isExpanded ? '1px solid #e6f7ff' : 'none',
                  transition: 'background-color 0.2s',
                }}
                onClick={() =>
                  setExpandedConflict(isExpanded ? null : index)
                }
              >
                <h3
                  style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}
                >
                  {agent1.title && `${agent1.title} `}
                  {agent1.name || '(No Name)'}
                </h3>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="#666"
                  style={{
                    transition: 'transform 0.2s',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                  }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {isExpanded && (
                <div style={{ padding: '1rem', backgroundColor: '#fafafa' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table>
                      <thead>
                        <tr
                          style={{
                            backgroundColor: '#fafafa',
                            borderBottom: '2px solid #f0f0f0',
                          }}
                        >
                          <th
                            style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              fontWeight: 600,
                            }}
                          >
                            Attribute
                          </th>
                          <th
                            style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              fontWeight: 600,
                            }}
                          >
                            Agent 1
                          </th>
                          <th
                            style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              fontWeight: 600,
                            }}
                          >
                            Agent 2
                          </th>
                          <th
                            style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              fontWeight: 600,
                            }}
                          >
                            Merge Result
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderRow({
                          field: 'title',
                          label: 'Title',
                          agent1,
                          agent2,
                          conflictIndex: index,
                          selectedValues,
                          handleSelectionChange,
                        })}
                        {renderRow({
                          field: 'name',
                          label: 'Name',
                          agent1,
                          agent2,
                          conflictIndex: index,
                          selectedValues,
                          handleSelectionChange,
                        })}
                        {renderRow({
                          field: 'address',
                          label: 'Address',
                          agent1,
                          agent2,
                          conflictIndex: index,
                          selectedValues,
                          handleSelectionChange,
                        })}
                        {renderRow({
                          field: 'email',
                          label: 'Email',
                          agent1,
                          agent2,
                          conflictIndex: index,
                          selectedValues,
                          handleSelectionChange,
                        })}
                        {renderRow({
                          field: 'blacklist',
                          label: 'Blacklisted',
                          agent1,
                          agent2,
                          conflictIndex: index,
                          selectedValues,
                          handleSelectionChange,
                          isBoolean: true,
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '12px',
                      marginTop: '1.5rem',
                    }}
                  >
                    <button
                      onClick={() => setExpandedConflict(null)}
                      className="button-cancel-merge"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        width="16"
                        height="16"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      Cancel
                    </button>

                    <button
                      onClick={() => handleMarkNoMerge(index)}
                      disabled={markingNoMerge[index]}
                      className="button-merge"
                    >
                      {markingNoMerge[index] ? (
                        <>
                          <div
                            style={{
                              width: '16px',
                              height: '16px',
                              border: '2px solid rgba(0, 0, 0, 0.1)',
                              borderRadius: '50%',
                              borderTop: '2px solid #bfbfbf',
                              animation: 'spin 1s linear infinite',
                            }}
                          ></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            width="16"
                            height="16"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          Mark as No Merge
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => handleMerge(index)}
                      disabled={processingMerges[index]}
                      className="button-bin"
                    >
                      {processingMerges[index] ? (
                        <>
                          <div
                            style={{
                              width: '16px',
                              height: '16px',
                              border:
                                '2px solid rgba(255, 255, 255, 0.3)',
                              borderRadius: '50%',
                              borderTop: '2px solid #fff',
                              animation: 'spin 1s linear infinite',
                            }}
                          ></div>
                          Merging...
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            width="16"
                            height="16"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          Confirm Merge
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AgentMergeConflicts;
